import { useEffect, useContext, useState } from "react";
import useAppMain from "src/hooks/useAppMain";
import {
  CLIENT_ROUTES,
  CLIENT_TAB_LIST,
  PATH_NAMES,
  monthArray,
  weeksArray,
} from "src/utils/constants";
import {
  Button,
  Flex,
  Text,
  Loader,
  Divider,
  Box,
  Progress,
  Avatar,
  Skeleton,
  useMantineTheme,
} from "@mantine/core";
import {
  useEnrollToMeetingMutation,
  useGetEventDetailLazyQuery,
  useGetValidCreditAllotmemtsLazyQuery,
  usePullFromMeetingMutation,
} from "src/generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import {
  TimeDiffType,
  getDateDiff,
  getTimeInAMPMFormat,
} from "src/utils/dateHelper";
import { AppContext } from "src/api/AppContext";
import { useScheduleEventContext } from "src/api/context/ScheduleEventContext";
import CreditSelection from "./CreditSelection";

const ProgressBar = (current: number, max: number, label?: string) => {
  const showCount = `${current} of ${max}`;
  const value = ~~((current / max) * 100);
  const color =
    value === 100
      ? "#FFD1D1"
      : value < 20
      ? "#C4F8AB"
      : value < 70
      ? "#FFDEAD"
      : "#FFD1D1";

  return (
    <Box sx={{ position: "relative" }} mt={7}>
      <Progress value={value} size={17} radius="xl" color={color} />
      <Text sx={{ position: "absolute", top: -3, left: "45%" }} size={14}>
        {showCount}
        {label}
      </Text>
    </Box>
  );
};

const DisplayDate = (startAt: Date, endAt: Date) => {
  const _start = new Date(startAt);
  return (
    <Text size={14} opacity={0.9}>
      {weeksArray[_start.getDay()]}, {_start.getDate()}{" "}
      {monthArray[_start.getMonth()]} | {getTimeInAMPMFormat(startAt)} -{" "}
      {getTimeInAMPMFormat(endAt)}
    </Text>
  );
};

function EventDetail() {
  useAppMain(CLIENT_TAB_LIST.MY_CALENDAR, true);
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { currentClient } = useContext(AppContext);
  const { setLocationService, setPrivateSession } = useScheduleEventContext();
  const theme = useMantineTheme();
  const [myCredit, setMyCredit] = useState("");

  useEffect(() => {
    meetingId && getEventDetail({ variables: { id: meetingId } });
  }, [meetingId]);

  const [getEventDetail, { data, loading }] = useGetEventDetailLazyQuery({});
  const {
    id,
    instructor,
    room,
    isWaitlistEnabled,
    location,
    description,
    waitlistMax,
    attendees,
    waitlistees,
    maxOccupancy,
    startAt,
    endAt,
    name,
    service,
    privateSession,
  } = data?.meetingOne || {};

  const isDisabled =
    getDateDiff(new Date(), new Date(startAt), TimeDiffType.hours) <= 24;
  const isEnroll = window.location.pathname.includes(PATH_NAMES.ENROLL);
  const isConfirmation = window.location.pathname.includes(
    PATH_NAMES.CONFIRMATION
  );

  useEffect(() => {
    currentClient &&
      (service || privateSession) &&
      getCredits({
        variables: {
          clientId: currentClient.id,
          locationServiceId: service?.id,
          privateSessionId: privateSession?.id,
        },
      });
  }, [currentClient, service, privateSession]);

  const [getCredits, { data: myCredits, loading: creditsLoading }] =
    useGetValidCreditAllotmemtsLazyQuery();

  const [cancelEnrollment, { loading: loadingBtn }] =
    usePullFromMeetingMutation({
      onCompleted() {
        navigate(-1);
      },
    });

  const [enrollClient, { loading: loadingEnroll }] = useEnrollToMeetingMutation(
    {
      onCompleted() {
        navigate(-5);
      },
    }
  );

  const handleCancelEnrollment = () => {
    if (id && currentClient)
      cancelEnrollment({
        variables: {
          meetingId: id,
          clientId: currentClient.id,
          cancelEnrollment: true,
        },
      });
  };

  const handleEnrollment = () => {
    navigate(`/${CLIENT_ROUTES.MY_CALENDAR}/${PATH_NAMES.CONFIRMATION}/${id}`);
  };

  const handleConfirmEnrollment = () => {
    if (id && currentClient)
      enrollClient({
        variables: {
          meetingId: id,
          clientId: currentClient.id,
          creditAllotmentId: myCredit,
        },
      });
  };

  const handleBuyCredits = () => {
    if (service?.id || privateSession?.id) {
      service?.id && setLocationService(service);
      privateSession?.id && setPrivateSession(privateSession);
      navigate(`/${CLIENT_ROUTES.SHOPPING_CART}`);
    }
  };

  if (loading)
    return <Loader width={"100%"} mt={"xl"} sx={{ margin: "auto" }} />;
  return (
    <Flex
      direction={"column"}
      justify={"space-between"}
      sx={{ height: "100%", color: theme.colors.secondaryTextColor[6] }}
    >
      <Box>
        <Text size={18} fw={500}>
          Event Overview
        </Text>
        <Divider />

        <Box mt={5}>
          <Text fw={500}>{name}</Text>
          {startAt && DisplayDate(startAt, endAt)}
          <Text size={14} opacity={0.9}>
            {location?.name} | {room?.name}
          </Text>
          <Text size={14} opacity={0.9}>
            Instructor |{" "}
            {`${instructor?.user.firstName} ${instructor?.user.lastName}`}
          </Text>
        </Box>

        {isConfirmation ? (
          <CreditSelection
            myCredits={myCredits?.getValidCreditAllotmemts || []}
            myCredit={myCredit}
            setMyCredit={setMyCredit}
            loading={creditsLoading}
          />
        ) : (
          <Box mt={"sm"}>
            <Text size={18} fw={500}>
              Enrollment
            </Text>
            <Divider />
            {maxOccupancy && ProgressBar(attendees!.length, maxOccupancy!)}

            {isWaitlistEnabled &&
              ProgressBar(waitlistees!.length, waitlistMax!, "in Waitlist")}

            <Box mt={"sm"}>
              <Text size={18} fw={500}>
                Details
              </Text>
              <Divider />

              <Flex mt={"sm"} gap={12} align={"center"}>
                <Flex direction={"column"} align={"center"}>
                  <Avatar
                    radius={55}
                    src={instructor?.user.profilePicUrl || ""}
                    sx={{ width: 95, height: 95, border: `1px solid #868686` }}
                  />
                  <Text fw={500} size={12} opacity={0.9}>
                    {`${instructor?.user.firstName} ${instructor?.user.lastName}`}
                  </Text>
                  <Text fw={600} size={10} opacity={0.9}>
                    Instructor
                  </Text>
                </Flex>

                <Text size={14} fw={description ? 400 : 700} opacity={0.9}>
                  {description || "No description Added"}
                </Text>
              </Flex>
            </Box>
          </Box>
        )}
      </Box>

      <Box mt={"xs"}>
        {!(isEnroll || isConfirmation) ? (
          <>
            <Text size={12} opacity={0.9}>
              Please contact us to cancel your enrollment within 24 hours of the
              session start.
            </Text>
            <Button
              mt={5}
              fullWidth
              bg="#D15959"
              loading={loadingBtn}
              onClick={handleCancelEnrollment}
              disabled={isDisabled}
            >
              Cancel My Enrollment
            </Button>
          </>
        ) : (
          <>
            {isConfirmation && (
              <Text size={12} opacity={0.9}>
                By choosing to continue you agree to all cancellation and class
                policies and fees.
              </Text>
            )}
            {creditsLoading ? (
              <Skeleton height={40} />
            ) : (
              <Button
                mt={5}
                fullWidth
                loading={loadingEnroll}
                disabled={isConfirmation && !myCredit}
                onClick={
                  !myCredits?.getValidCreditAllotmemts.length
                    ? handleBuyCredits
                    : isConfirmation
                    ? handleConfirmEnrollment
                    : handleEnrollment
                }
              >
                {!myCredits?.getValidCreditAllotmemts.length
                  ? "Buy Credits"
                  : "Enroll"}
              </Button>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
}

export default EventDetail;
